import React, {
  useState,
  useEffect,
} from "react";

import { useNavigate } from "react-router-dom";

import axios from "axios";

import PhoneInput from "react-phone-input-2";

import "react-phone-input-2/lib/style.css";

import "./PaymentPage.css";


// =====================================================
// PAYMENT PAGE
// =====================================================

const PaymentPage = () => {
  const navigate = useNavigate();

  // ===================================================
  // PAGE INITIALIZATION
  // ===================================================

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ===================================================
  // HIDE NAVBAR / FOOTER
  // ===================================================

  useEffect(() => {
    const navbar =
      document.querySelector(
        "header, .navbar"
      );

    const footer =
      document.querySelector("footer");

    if (navbar) {
      navbar.style.display = "none";
    }

    if (footer) {
      footer.style.display = "none";
    }

    window.scrollTo(0, 0);

    return () => {
      if (navbar) {
        navbar.style.display = "";
      }

      if (footer) {
        footer.style.display = "";
      }
    };
  }, []);

  // ===================================================
  // PRODUCT CONFIGURATION
  // ===================================================

  const SERVICE_NAME =
    "Forge Your Peak - Ignite";

  // Normal price in rupees
  const NORMAL_PRICE = 8999;

  // Normal price in paise
  const NORMAL_AMOUNT = 899900;

  // ===================================================
  // OFFER CODE FROM URL
  //
  // Normal:
  // /ignite-payment
  //
  // Offer:
  // /ignite-payment?offer=ignite7999
  // ===================================================

  const searchParams =
    new URLSearchParams(
      window.location.search
    );

  const OFFER_CODE =
    searchParams
      .get("offer")
      ?.trim()
      .toLowerCase() || null;

  // ===================================================
  // OFFER STATE
  // ===================================================

  const [
    offerData,
    setOfferData,
  ] = useState(null);

  const [
    isOfferLoading,
    setIsOfferLoading,
  ] = useState(
    Boolean(OFFER_CODE)
  );

  const [
    offerError,
    setOfferError,
  ] = useState("");

  // ===================================================
  // PAYMENT STATE
  // ===================================================

  const [
    isLoading,
    setIsLoading,
  ] = useState(false);

  // ===================================================
  // FORM STATE
  // ===================================================

  const [
    formData,
    setFormData,
  ] = useState({
    name: "",
    email: "",
    phone: "",
  });

  // ===================================================
  // FETCH OFFER
  //
  // IMPORTANT:
  // Backend decides whether offer is active.
  // Frontend never decides the price.
  // ===================================================

  useEffect(() => {
    const fetchOffer = async () => {
      // No offer code
      if (!OFFER_CODE) {
        setOfferData(null);
        setIsOfferLoading(false);
        return;
      }

      try {
        setIsOfferLoading(true);
        setOfferError("");

        const response =
          await axios.get(
            `/api/offers/${encodeURIComponent(
              OFFER_CODE
            )}`
          );

        const data =
          response.data;

        if (
          !data ||
          !data.success ||
          !data.offer
        ) {
          throw new Error(
            data?.message ||
              "Offer is unavailable."
          );
        }

        // ---------------------------------------------
        // Verify service
        // ---------------------------------------------

        if (
          data.offer.service !==
          SERVICE_NAME
        ) {
          throw new Error(
            "This offer is not valid for this service."
          );
        }

        // ---------------------------------------------
        // Verify active status
        // ---------------------------------------------

        if (
          data.offer.isActive !==
          true
        ) {
          throw new Error(
            "This offer is currently unavailable."
          );
        }

        // ---------------------------------------------
        // Validate amounts
        // ---------------------------------------------

        const originalAmount =
          Number(
            data.offer.originalAmount
          );

        const offerAmount =
          Number(
            data.offer.offerAmount
          );

        if (
          !Number.isInteger(
            originalAmount
          ) ||
          originalAmount <= 0
        ) {
          throw new Error(
            "Invalid original offer price."
          );
        }

        if (
          !Number.isInteger(
            offerAmount
          ) ||
          offerAmount <= 0
        ) {
          throw new Error(
            "Invalid offer price."
          );
        }

        if (
          offerAmount >=
          originalAmount
        ) {
          throw new Error(
            "Invalid offer configuration."
          );
        }

        // ---------------------------------------------
        // Store offer
        // ---------------------------------------------

        setOfferData({
          ...data.offer,

          originalAmount,

          offerAmount,
        });

      } catch (error) {
        console.error(
          "❌ Offer fetch error:",
          error
        );

        setOfferData(null);

        setOfferError(
          error.response?.data?.message ||
            error.message ||
            "This offer is currently unavailable."
        );
      } finally {
        setIsOfferLoading(false);
      }
    };

    fetchOffer();
  }, [OFFER_CODE]);

  // ===================================================
  // FINAL DISPLAY PRICE
  // ===================================================

  const PRODUCT_PRICE =
    offerData?.isActive
      ? offerData.offerAmount /
        100
      : NORMAL_PRICE;

  // ===================================================
  // ORIGINAL PRICE
  // ===================================================

  const ORIGINAL_PRICE =
    offerData?.isActive
      ? offerData.originalAmount /
        100
      : NORMAL_PRICE;

  // ===================================================
  // IS OFFER ACTIVE
  // ===================================================

  const isOfferActive =
    Boolean(
      OFFER_CODE &&
        offerData &&
        offerData.isActive === true
    );

  // ===================================================
  // FORM CHANGE
  // ===================================================

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ===================================================
  // PHONE CHANGE
  // ===================================================

  const handlePhoneChange = (
    phone,
    country
  ) => {
    setFormData((prev) => ({
      ...prev,
      phone,
    }));

    console.log(
      "Selected country:",
      country
    );
  };

  // ===================================================
  // PAYMENT
  // ===================================================

  const handlePayment =
    async () => {
      try {
        // =============================================
        // OFFER LOADING
        // =============================================

        if (isOfferLoading) {
          alert(
            "Checking offer availability. Please wait."
          );

          return;
        }

        // =============================================
        // OFFER INVALID
        // =============================================

        if (
          OFFER_CODE &&
          !isOfferActive
        ) {
          alert(
            offerError ||
              "This offer is currently unavailable."
          );

          return;
        }

        // =============================================
        // NAME
        // =============================================

        if (
          !formData.name.trim()
        ) {
          alert(
            "Please enter your full name."
          );

          return;
        }

        // =============================================
        // EMAIL
        // =============================================

        if (
          !formData.email.trim()
        ) {
          alert(
            "Please enter your email address."
          );

          return;
        }

        // =============================================
        // PHONE
        // =============================================

        if (
          !formData.phone.trim()
        ) {
          alert(
            "Please enter your phone number."
          );

          return;
        }

        // =============================================
        // RAZORPAY CHECK
        // =============================================

        if (!window.Razorpay) {
          alert(
            "Razorpay Checkout is not loaded."
          );

          return;
        }

        setIsLoading(true);

        // =============================================
        // FINAL OFFER CODE
        // =============================================

        const finalOffer =
          isOfferActive
            ? OFFER_CODE
            : null;

        // =============================================
        // CREATE RAZORPAY ORDER
        // =============================================

        const response =
          await axios.post(
            "/api/razorpay/create-order",
            {
              name:
                formData.name.trim(),

              email:
                formData.email
                  .trim()
                  .toLowerCase(),

              phone:
                formData.phone,

              service:
                SERVICE_NAME,

              offer:
                finalOffer,
            }
          );

        const data =
          response.data;

        console.log(
          "Razorpay Order Response:",
          data
        );

        // =============================================
        // BACKEND VALIDATION
        // =============================================

        if (!data.success) {
          throw new Error(
            data.message ||
              "Unable to create payment order."
          );
        }

        if (
          !data.order ||
          !data.order.id
        ) {
          throw new Error(
            "Invalid Razorpay order response."
          );
        }

        // =============================================
        // IMPORTANT
        //
        // Backend is the source of truth.
        // Do not trust frontend calculated amount.
        // =============================================

        const backendAmount =
          Number(
            data.order.amount
          );

        if (
          !Number.isInteger(
            backendAmount
          ) ||
          backendAmount <= 0
        ) {
          throw new Error(
            "Invalid payment amount received from server."
          );
        }

        // =============================================
        // RAZORPAY CHECKOUT OPTIONS
        // =============================================

        const options = {
          key:
            data.keyId,

          amount:
            backendAmount,

          currency:
            data.order.currency ||
            "INR",

          name:
            "Forge Your Peak",

          description:
            SERVICE_NAME,

          order_id:
            data.order.id,

          prefill: {
            name:
              formData.name.trim(),

            email:
              formData.email
                .trim()
                .toLowerCase(),

            contact:
              formData.phone,
          },

          notes: {
            service:
              SERVICE_NAME,

            offer:
              finalOffer ||
              "normal",
          },

          theme: {
            color:
              "#242951",
          },

          // =========================================
          // PAYMENT SUCCESS
          // =========================================

          handler:
            async function (
              paymentResponse
            ) {
              try {
                console.log(
                  "✅ Razorpay Payment Successful"
                );

                console.log(
                  "Payment ID:",
                  paymentResponse
                    .razorpay_payment_id
                );

                console.log(
                  "Order ID:",
                  paymentResponse
                    .razorpay_order_id
                );

                // ===================================
                // VERIFY PAYMENT
                // ===================================

                const verifyResponse =
                  await axios.post(
                    "/api/razorpay/verify-payment",
                    {
                      razorpay_payment_id:
                        paymentResponse
                          .razorpay_payment_id,

                      razorpay_order_id:
                        paymentResponse
                          .razorpay_order_id,

                      razorpay_signature:
                        paymentResponse
                          .razorpay_signature,

                      name:
                        formData.name.trim(),

                      email:
                        formData.email
                          .trim()
                          .toLowerCase(),

                      phone:
                        formData.phone,

                      service:
                        SERVICE_NAME,

                      offer:
                        finalOffer,
                    }
                  );

                const verifyData =
                  verifyResponse.data;

                console.log(
                  "Payment Verification Response:",
                  verifyData
                );

                // ===================================
                // VERIFY RESPONSE
                // ===================================

                if (
                  !verifyData.success
                ) {
                  throw new Error(
                    verifyData.message ||
                      "Payment verification failed."
                  );
                }

                // ===================================
                // PAYMENT VERIFIED
                // ===================================

                console.log(
                  "✅ Payment verified successfully."
                );

                // ===================================
                // META PURCHASE
                // ===================================

                if (window.fbq) {
                  window.fbq(
                    "trackSingle",

                    "2060088607979588",

                    "Purchase",

                    {
                      content_category:
                        "Product Purchase",

                      content_name:
                        SERVICE_NAME,

                      value:
                        PRODUCT_PRICE,

                      currency:
                        "INR",

                      contents: [
                        {
                          id:
                            isOfferActive
                              ? OFFER_CODE
                              : "fyp-ignite",

                          quantity: 1,
                        },
                      ],

                      content_type:
                        "product",
                    }
                  );

                  console.log(
                    "✅ Meta Purchase event tracked."
                  );
                }

                // ===================================
                // SUCCESS MESSAGE
                // ===================================

                alert(
                  "Payment successful! Your access has been activated."
                );

                // ===================================
                // REDIRECT
                // ===================================

                navigate(
                  "/dashboard-login",
                  {
                    replace: true,
                  }
                );

              } catch (error) {
                console.error(
                  "❌ Payment verification error:",
                  error
                );

                alert(
                  error.response?.data
                    ?.message ||
                    error.message ||
                    "Payment verification failed. Please contact support."
                );

              } finally {
                setIsLoading(false);
              }
            },

          // =========================================
          // CHECKOUT CLOSED
          // =========================================

          modal: {
            ondismiss:
              function () {
                console.log(
                  "Razorpay Checkout closed."
                );

                setIsLoading(false);
              },
          },
        };

        // =============================================
        // CREATE RAZORPAY INSTANCE
        // =============================================

        const razorpay =
          new window.Razorpay(
            options
          );

        // =============================================
        // PAYMENT FAILED
        // =============================================

        razorpay.on(
          "payment.failed",
          function (response) {
            console.error(
              "❌ Razorpay Payment Failed:",
              response.error
            );

            alert(
              response.error?.description ||
                "Payment failed. Please try again."
            );

            setIsLoading(false);
          }
        );

        // =============================================
        // OPEN RAZORPAY
        // =============================================

        razorpay.open();

      } catch (error) {
        console.error(
          "❌ Payment Error:",
          error
        );

        const message =
          error.response?.data
            ?.message ||
          error.message ||
          "Something went wrong while starting the payment.";

        alert(message);

        setIsLoading(false);
      }
    };

  // ===================================================
  // RENDER
  // ===================================================

  return (
    <div className="payment-page">

      {/* =================================================
          LEFT SECTION
      ================================================= */}

      <section className="payment-left">

        <div className="payment-left-inner">

          {/* Logo */}

          <div className="company-logo">

            <img
              src="/Logo.png"
              alt="Forge Your Peak"
            />

          </div>

          {/* Product Header */}

          <div className="product-header">

            <h1>
              Forge Your Peak – Ignite
            </h1>

            <p>
              By ForgeYourPeak
            </p>

            {/* =========================================
                PRICE
            ========================================= */}

            <div className="product-price">

              {isOfferActive ? (
                <>
                  <span
                    style={{
                      textDecoration:
                        "line-through",

                      color:
                        "#6b7280",

                      fontSize:
                        "22px",

                      marginRight:
                        "12px",
                    }}
                  >
                    ₹
                    {ORIGINAL_PRICE}
                  </span>

                  <span>
                    ₹
                    {PRODUCT_PRICE}
                  </span>
                </>
              ) : (
                <>
                  ₹
                  {NORMAL_PRICE}
                </>
              )}

            </div>

            {/* =========================================
                OFFER LOADING
            ========================================= */}

            {isOfferLoading && (
              <div
                style={{
                  marginTop:
                    "10px",

                  color:
                    "#6b7280",

                  fontSize:
                    "14px",
                }}
              >
                Checking offer...
              </div>
            )}

            {/* =========================================
                ACTIVE OFFER
            ========================================= */}

            {isOfferActive && (
              <div
                style={{
                  marginTop:
                    "8px",

                  display:
                    "inline-block",

                  padding:
                    "6px 12px",

                  borderRadius:
                    "6px",

                  background:
                    "#fff1f2",

                  color:
                    "#d60a17",

                  fontWeight:
                    "700",

                  fontSize:
                    "14px",
                }}
              >
                SPECIAL OFFER
              </div>
            )}

            {/* =========================================
                INVALID / INACTIVE OFFER
            ========================================= */}

            {OFFER_CODE &&
              !isOfferLoading &&
              !isOfferActive && (
                <div
                  style={{
                    marginTop:
                      "10px",

                    padding:
                      "10px 12px",

                    borderRadius:
                      "6px",

                    background:
                      "#fff1f2",

                    color:
                      "#b91c1c",

                    fontWeight:
                      "600",

                    fontSize:
                      "14px",
                  }}
                >
                  {offerError ||
                    "This offer is currently unavailable."}
                </div>
              )}

          </div>

          {/* Product Image */}

          <div className="product-image-wrapper">

            <img
              src="/Ignite-Product.png"
              alt="Forge Your Peak Ignite"
              className="product-image"
            />

          </div>

          {/* Product Description */}

          <div className="product-description">

            <p>
              90 days of weekly
              neuro-performance coaching
              designed to improve the six
              hidden drivers of athletic
              performance:
            </p>

            <ul>
              <li>
                Vision Clarity
              </li>

              <li>
                Reaction Speed
              </li>

              <li>
                Decision Making
              </li>

              <li>
                Anticipation
              </li>

              <li>
                Memory
              </li>

              <li>
                Recovery
              </li>
            </ul>

            <p>
              Using our 10-metric assessment
              system and 2 personalized
              performance trackers, you'll
              identify the bottlenecks limiting
              your game and systematically
              improve them through structured
              training.
            </p>

            <p>
              By Day 90, you'll have measurable
              data showing your progress across
              all six pillars. Most athletes see
              their largest improvements in
              their primary performance
              bottlenecks, with average gains
              of up to 80 milliseconds in
              reaction-based metrics and
              noticeable improvements in
              consistency, confidence, and
              execution under pressure.
            </p>

            <p>
              Your membership includes:
            </p>

            <ul>

              <li>
                Weekly coaching sessions
                (live and recorded)
              </li>

              <li>
                10-metric baseline assessment
              </li>

              <li>
                Personalized performance
                dashboard
              </li>

              <li>
                Access to the private athlete
                community
              </li>

              <li>
                Step-by-step courses across
                all six performance pillars
              </li>

              <li>
                Progress reviews and ongoing
                coaching support
              </li>

            </ul>

            <p>
              Join a growing community of
              athletes committed to gaining an
              edge where performance truly
              begins—the brain.
            </p>

          </div>

        </div>

      </section>

      {/* =================================================
          RIGHT SECTION
      ================================================= */}

      <section className="payment-right">

        <div className="payment-right-inner">

          <h2>
            Payment details
          </h2>

          <p className="payment-subtitle">
            Complete your purchase by
            providing your payment details.
          </p>

          {/* =================================================
              BILLING
          ================================================= */}

          <div className="billing-card">

            <h3>
              Billing information
            </h3>

            <div className="billing-fields">

              {/* Name */}

              <input
                type="text"
                name="name"
                placeholder="Full name"
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
              />

              {/* Email */}

              <input
                type="email"
                name="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />

              {/* Phone */}

              <div className="phone-wrapper">

                <PhoneInput
                  country="in"

                  value={
                    formData.phone
                  }

                  onChange={
                    handlePhoneChange
                  }

                  enableSearch={true}

                  countryCodeEditable={
                    false
                  }

                  placeholder={
                    "Phone number"
                  }

                  inputProps={{
                    name:
                      "phone",

                    required:
                      true,
                  }}

                  containerClass={
                    "custom-phone-container"
                  }

                  inputClass={
                    "custom-phone-input"
                  }

                  buttonClass={
                    "custom-phone-button"
                  }

                  dropdownClass={
                    "custom-phone-dropdown"
                  }

                  disabled={
                    isLoading
                  }
                />

              </div>

            </div>

          </div>

          {/* =================================================
              ORDER SUMMARY
          ================================================= */}

          <div className="order-card">

            <div className="order-top-line"></div>

            <div className="order-content">

              <h3>
                Service
              </h3>

              <div className="service-row">

                <span>
                  {SERVICE_NAME}
                </span>

                <strong>
                  ₹
                  {PRODUCT_PRICE}
                  .00
                </strong>

              </div>

              {/* =========================================
                  OFFER DISPLAY
              ========================================= */}

              {isOfferActive && (
                <div
                  style={{
                    marginTop:
                      "8px",

                    fontSize:
                      "13px",

                    color:
                      "#16a34a",

                    fontWeight:
                      "600",
                  }}
                >
                  Special offer applied
                </div>
              )}

              <div className="divider"></div>

              <div className="total-row">

                <span>
                  Amount to be paid :
                </span>

                <strong>
                  ₹
                  {PRODUCT_PRICE}
                  .00
                </strong>

              </div>

            </div>

          </div>

          {/* =================================================
              PAY BUTTON
          ================================================= */}

          <button
            type="button"
            className="pay-button"
            onClick={
              handlePayment
            }
            disabled={
              isLoading ||
              isOfferLoading ||
              Boolean(
                OFFER_CODE &&
                  !isOfferActive
              )
            }
          >
            {isLoading
              ? "Creating payment..."
              : isOfferLoading
              ? "Checking offer..."
              : `Proceed to pay ₹${PRODUCT_PRICE}.00`}
          </button>

        </div>

      </section>

    </div>
  );
};

export default PaymentPage;